import { useEffect, useState } from "react";
import { CircularProgress, Grid, Link } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { getLatestPaymentsQuery } from "@query";
import { universalDateParser, formatPriceWithCurrency } from "@utils";
import { Button } from "@components";
import { PaymentHistoryDialog } from "../PaymentHistoryDialog";
import useStyle from "./SubscriptionSections.style";
import { EmailInvoicesDialog } from "@views/Utility/Subscription/components/EmailInvoicesDialog";

export const SectionPayments = () => {
  const { t } = useTranslation();
  const classes = useStyle();

  const [isLoading, setLoading] = useState(false);
  const [historyDialogOpened, openHistoryDialog] = useState(false);
  const [invoiceEmailDialogOpened, openInvoiceEmailDialog] = useState(false);
  const [latestPayments, setPayments] = useState([]);

  const loadData = async () => {
    setLoading(true);
    const h = await getLatestPaymentsQuery();
    setPayments(h);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Grid item container xs={12} md={4} className={classes.box}>
      <p className="font-bold text-xl">
        {t("views.subscription.titles.payments")}
      </p>
      {
        isLoading &&
        <CircularProgress
          aria-label="progress indicator"
          size={40}
          color="primary"
          className={classes.spinner}
        />
      }
      {
        !isLoading && !latestPayments?.length &&
        <p className="transparent-2">
          {t("views.subscription.no_payments")}
        </p>
      }
      {
        !isLoading && latestPayments?.length > 0 &&
        <Grid item container direction="column" className="w-full">
          {/* Nagłówki kolumn */}
          <Grid item container justifyContent="space-between" alignItems="center" className="mb-1">
            <p className="transparent-2 text-sm">
              {t("views.subscription.type")}
            </p>
            <p className="transparent-2 text-sm">
              {t("views.subscription.amount_gross")}
            </p>
          </Grid>

          {/* Lista płatności */}
          {latestPayments.map(({
            id,
            description,
            createdAt,
            amount,
            status,
            currency,
            paid,
            refunded,
            amountRefunded,
            stripeInvoicePdf,
            stripeInvoiceUrl,
          }) => {
            const hasInvoice = Boolean(stripeInvoiceUrl);
            const name = description || t("views.subscription.sub");

            return (
              <Grid
                key={id}
                item container
                justifyContent="space-between"
                alignItems="center"
                wrap="nowrap"
                className="my-0-5"
              >
                <Grid item container direction="column" style={{ flex: 1, minWidth: 0 }}>
                  {
                    hasInvoice
                      ? <Link
                        href={paid ? stripeInvoicePdf : stripeInvoiceUrl}
                        target="_blank"
                        className="text-md ellipsis"
                      >
                        { name }
                      </Link>
                      : <p className="text-md ellipsis">{ name }</p>
                  }
                  <p className="text-sm text-lighter">
                    {format(universalDateParser(createdAt), "dd.MM.yyyy")}
                    <i className="ml-1">
                      {t(`views.subscription.payment_status.${status}`)}
                    </i>
                  </p>
                  {refunded && (
                    <p className="text-xs color-error">
                      {t("views.subscription.payment_status.refunded")}: {formatPriceWithCurrency(amountRefunded, currency)}
                    </p>
                  )}
                </Grid>
                <strong className="text-lg text-nowrap ml-2">
                  {formatPriceWithCurrency(amount, currency)}
                </strong>
              </Grid>
            );
          })}
        </Grid>
      }
      <Grid item container justifyContent="space-between">
        <Button
          variant="outlined"
          size="small"
          disabled={isLoading || !latestPayments.length}
          onClick={() => openHistoryDialog(true)}
        >
          {t("views.subscription.actions.show_history")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={isLoading}
          onClick={() => openInvoiceEmailDialog(true)}
        >
          {t("views.subscription.actions.email_invoices")}
        </Button>
      </Grid>
      <PaymentHistoryDialog
        open={historyDialogOpened}
        onClose={() => openHistoryDialog(false)}
      />
      <EmailInvoicesDialog
        open={invoiceEmailDialogOpened}
        onClose={() => openInvoiceEmailDialog(false)}
      />
    </Grid>
  );
};

SectionPayments.propTypes = {};
